export class HelperFunctions {
  private static readonly MONTH_LABELS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ] as const;

  private static readonly MONTH_INDEX: Record<string, number> = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  static toStringOrNull(value: string): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  static encodeUrlSegment(value: string): string {
    return encodeURIComponent(value.trim());
  }

  static buildPathEndpoint(endpoint: string, segments: readonly (string | undefined)[]): string {
    const base = endpoint.trim().replace(/\/+$/, '');
    const encodedSegments = segments.map((segment) => this.encodeUrlSegment(segment ?? ''));
    return `${base}/${encodedSegments.join('/')}`;
  }

  static formatDateToIsoDate(value: Date | string, options?: { includeTime?: boolean }): string {
    const parsedDate = this.parseDateValue(value);
    if (!parsedDate) {
      return '';
    }

    const includeTime = options?.includeTime ?? true;

    const day = parsedDate.getDate().toString().padStart(2, '0');
    const month = this.MONTH_LABELS[parsedDate.getMonth()] ?? '';
    const year = parsedDate.getFullYear();
    const dateLabel = `${day}-${month}-${year}`;

    if (!includeTime || typeof value !== 'string') {
      return dateLabel;
    }

    const timeLabel = this.extractTimeLabel(value);
    return timeLabel ? `${dateLabel} ${timeLabel}` : dateLabel;
  }

  // Keeps original API time values when they are present in the input string.
  private static extractTimeLabel(value: string): string {
    const match = value.trim().match(/\b\d{1,2}:\d{2}(?::\d{2})?(?:\s?[AaPp][Mm])?\b/);

    return match?.[0]?.trim() ?? '';
  }

  // Normalizes mixed API date formats into a valid Date instance before display formatting.
  private static parseDateValue(value: Date | string): Date | null {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const rawValue = value.trim();
    if (!rawValue) {
      return null;
    }

    const nativeParsedDate = new Date(rawValue);
    if (!Number.isNaN(nativeParsedDate.getTime())) {
      return nativeParsedDate;
    }

    const monthNameMatch = rawValue.match(/\b(\d{1,2})[-/\s]([A-Za-z]{3})[-/\s](\d{2}|\d{4})\b/);
    if (monthNameMatch) {
      const day = Number(monthNameMatch[1]);
      const monthIndex = this.MONTH_INDEX[monthNameMatch[2].toLowerCase()];
      const year = this.normalizeYear(Number(monthNameMatch[3]));

      if (monthIndex !== undefined) {
        return this.createValidDate(year, monthIndex, day);
      }
    }

    const dayFirstMatch = rawValue.match(/\b(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})\b/);
    if (dayFirstMatch) {
      const day = Number(dayFirstMatch[1]);
      const monthIndex = Number(dayFirstMatch[2]) - 1;
      const year = this.normalizeYear(Number(dayFirstMatch[3]));

      return this.createValidDate(year, monthIndex, day);
    }

    const isoLikeMatch = rawValue.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);
    if (isoLikeMatch) {
      const year = Number(isoLikeMatch[1]);
      const monthIndex = Number(isoLikeMatch[2]) - 1;
      const day = Number(isoLikeMatch[3]);

      return this.createValidDate(year, monthIndex, day);
    }

    return null;
  }

  // Rejects overflow dates while converting text-based date parts.
  private static createValidDate(year: number, monthIndex: number, day: number): Date | null {
    if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) {
      return null;
    }

    const date = new Date(year, monthIndex, day);
    if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) {
      return null;
    }

    return date;
  }

  // Expands short years so legacy 2-digit inputs still map to the intended century.
  private static normalizeYear(year: number): number {
    if (year >= 100) {
      return year;
    }

    return year >= 70 ? 1900 + year : 2000 + year;
  }

  static toCleanString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  static toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    return false;
  }

  static csvToList(value: unknown): readonly string[] {
    if (typeof value !== 'string') {
      return [];
    }

    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  // Returns the first non-empty value as text from a set of candidate keys.
  static readFirstValueAsText(
    source: Record<string, unknown> | null | undefined,
    keys: readonly string[],
    options?: { allowNumber?: boolean },
  ): string {
    if (!source) {
      return '';
    }

    const allowNumber = options?.allowNumber ?? false;

    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }

      if (allowNumber && typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }

    return '';
  }

  // Formats attachment size metadata and converts raw byte values into readable units.
  static formatAttachmentSize(value: unknown): string {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return this.formatBytesToSizeLabel(value);
    }

    if (typeof value !== 'string') {
      return '';
    }

    const normalized = value.trim();
    if (!normalized) {
      return '';
    }

    if (/^[0-9]+(?:\.[0-9]+)?$/.test(normalized)) {
      return this.formatBytesToSizeLabel(Number(normalized));
    }

    return normalized;
  }

  // Converts bytes to B, KB, MB, or GB while keeping a stable two-decimal precision.
  private static formatBytesToSizeLabel(bytesValue: number): string {
    const bytes = Math.max(0, bytesValue);
    if (bytes < 1024) {
      return `${bytes} B`;
    }

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(2)} KB`;
    }

    const mb = kb / 1024;
    if (mb < 1024) {
      return `${mb.toFixed(2)} MB`;
    }

    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  }

  // Extracts attachment sys_id from either query-string or ServiceNow file-path URL shapes.
  static resolveAttachmentSysIdFromHref(href: string | null | undefined): string {
    const normalizedHref = href?.trim() ?? '';
    if (!normalizedHref) {
      return '';
    }

    const querySysId = normalizedHref.match(/[?&]sys_id=([^&#]+)(?=[&#]|$)/i)?.[1]?.trim();
    if (querySysId) {
      return querySysId;
    }

    const pathSysId = normalizedHref.match(/\/attachment\/([^/?#]+)\/file(?:[?#]|$)/i)?.[1]?.trim();
    return pathSysId ?? '';
  }

  // Reads a stable user identifier (username/email) from mixed ServiceNow user payload shapes.
  static resolveUserIdFromPayloadUser(payloadUser: unknown): string {
    if (!payloadUser || typeof payloadUser !== 'object' || Array.isArray(payloadUser)) {
      return '';
    }

    const userRecord = payloadUser as Record<string, unknown>;
    const usernameField = userRecord['username'];

    if (usernameField && typeof usernameField === 'object' && !Array.isArray(usernameField)) {
      const usernameValue = this.readFirstValueAsText(usernameField as Record<string, unknown>, [
        'display_value',
        'value',
      ]);

      if (usernameValue) {
        return usernameValue;
      }
    }

    return this.readFirstValueAsText(userRecord, [
      'username',
      'user_name',
      'email',
      'user_email',
      'name',
    ]);
  }

  // Keeps upload target construction consistent across Action Insights and Transaction History.
  static buildUploadTargetContext(
    targetTable: string | null | undefined,
    targetSysId: string | null | undefined,
    userId?: string | null,
  ): { targetTable: string; targetSysId: string; userId?: string } | null {
    const resolvedTable = targetTable?.trim() ?? '';
    const resolvedSysId = targetSysId?.trim() ?? '';
    const resolvedUserId = userId?.trim() ?? '';

    if (!resolvedTable || !resolvedSysId) {
      return null;
    }

    return {
      targetTable: resolvedTable,
      targetSysId: resolvedSysId,
      ...(resolvedUserId ? { userId: resolvedUserId } : {}),
    };
  }

  // Uses the same label routing rule for upload actions in all action hosts.
  static isUploadActionLabel(label: string | null | undefined): boolean {
    const normalizedLabel = label?.trim().toLowerCase() ?? '';
    return normalizedLabel.includes('upload');
  }

  // Uses the same label routing rule for invoice-submit download actions in all action hosts.
  static isDownloadActionLabel(label: string | null | undefined): boolean {
    const normalizedLabel = label?.trim().toLowerCase() ?? '';
    return normalizedLabel.includes('download');
  }

  // Builds the invoice-submit condition once so all pages call data_download with the same query.
  static buildInvoiceSubmitDownloadCondition(recordSysId: string | null | undefined): string {
    const resolvedRecordSysId = recordSysId?.trim() ?? '';
    return resolvedRecordSysId ? `workflow_state=1^invoice_id=${resolvedRecordSysId}` : '';
  }
}
